import type * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Loader } from "@/ui/atoms";
import { cn } from "@/utils/utils";
import type { TApiOriginalPreview } from "@/apis/proda_finance/types";

/**
 * What the preview actually withheld, or null when it withheld nothing.
 *
 * The two axes are named separately because they fail independently: a short,
 * wide sheet returns every row and loses only columns, and describing that as
 * withheld rows sends the reader hunting for data that is already on screen.
 */
function truncationNotice(preview: TApiOriginalPreview): string | null {
    const parts: string[] = [];
    if (preview.truncated_rows) {
        parts.push(`${preview.rows.length} rows`);
    }
    if (preview.truncated_columns) {
        // The widest returned row, since spreadsheet rows are routinely ragged.
        const columns = preview.rows.reduce((widest, row) => Math.max(widest, row.length), 0);
        parts.push(`${columns} columns`);
    }
    if (parts.length === 0) {
        return null;
    }
    return `Showing the first ${parts.join(" and ")}. Download the file to see all of it.`;
}

export type TOriginalFilePreviewProps = {
    fileId: string;
    loadPreview: (fileId: string, sheet?: string) => Promise<TApiOriginalPreview>;
};

/**
 * The expanded preview beneath one row of the Original Files tab.
 *
 * Owns its own request state rather than reading a page-level slot: several rows
 * can be expanded at once, and a shared slot would let the last fetch to land
 * overwrite every other row's grid.
 *
 * Mounting is the trigger — the tab only renders this once the row is expanded,
 * so nothing is fetched for a file the user never opened.
 */
export function OriginalFilePreview(props: TOriginalFilePreviewProps): React.ReactElement {
    const { fileId, loadPreview } = props;
    const [preview, setPreview] = useState<TApiOriginalPreview | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [sheet, setSheet] = useState<string | undefined>(undefined);

    // Callers may not give `loadPreview` a stable identity across renders. Hold the
    // latest in a ref, updated by its own effect, so an unrelated re-render of the
    // host tab can't re-fire the fetch effect and issue a duplicate request.
    const loadPreviewRef = useRef(loadPreview);
    useEffect(() => {
        loadPreviewRef.current = loadPreview;
    }, [loadPreview]);

    // A different file makes any sheet chosen for the previous one meaningless —
    // reset it during render so a stale sheet name is never sent alongside a new
    // fileId. (Every panel instance is mounted for one fixed fileId today, so this
    // can't currently fire, but the component shouldn't depend on that from outside.)
    const [lastFileId, setLastFileId] = useState(fileId);
    if (fileId !== lastFileId) {
        setLastFileId(fileId);
        setSheet(undefined);
    }

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);
        loadPreviewRef
            .current(fileId, sheet)
            .then((result) => {
                // A sheet switch that resolves after the row was collapsed, or after
                // a later switch, must not overwrite what is on screen.
                if (!cancelled) setPreview(result);
            })
            .catch((cause: unknown) => {
                if (!cancelled) {
                    setError(cause instanceof Error ? cause : new Error("Preview failed"));
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [fileId, sheet]);

    if (isLoading && preview === null) {
        return (
            <div data-testid="original-preview-loading" className="flex justify-center py-6">
                <Loader size="sm" />
            </div>
        );
    }

    if (error !== null) {
        return (
            <p data-testid="original-preview-error" className="py-4 text-sm text-destructive">
                Could not load a preview of this file. You can still download it.
            </p>
        );
    }

    if (preview === null) {
        // Unreachable in practice: `isLoading` only clears after the fetch effect sets
        // either `preview` or `error` (and `error` is already handled above), never
        // on the cancelled path. Kept so TypeScript can narrow `preview` below.
        return <div data-testid="original-preview-loading" />;
    }

    const hasSheetTabs = preview.sheet_names.length > 1;
    const notice = truncationNotice(preview);

    return (
        <div className="space-y-2 pt-2">
            {hasSheetTabs && (
                <div className="flex flex-wrap gap-1">
                    {preview.sheet_names.map((name) => {
                        const isActive = name === (preview.sheet_name ?? "");
                        return (
                            <button
                                key={name}
                                type="button"
                                data-testid={`original-preview-sheet-${name}`}
                                onClick={() => setSheet(name)}
                                className={cn(
                                    "rounded-md border px-2 py-1 text-xs",
                                    isActive
                                        ? "border-primary font-medium text-primary"
                                        : "border-border text-muted-foreground hover:text-foreground",
                                )}
                            >
                                {name}
                            </button>
                        );
                    })}
                </div>
            )}

            {preview.unavailable_reason !== null ? (
                <p
                    data-testid="original-preview-unavailable"
                    className="py-3 text-sm text-muted-foreground"
                >
                    {preview.unavailable_reason}
                </p>
            ) : (
                <>
                    <div className="max-h-96 overflow-auto rounded-md border border-border">
                        <table
                            data-testid="original-preview-grid"
                            className="w-full border-collapse text-xs"
                        >
                            <tbody>
                                {preview.rows.map((row, rowIndex) => (
                                    // Row order is the file's order and never reorders, so the
                                    // index is a stable identity here — the cells carry no state.
                                    <tr key={rowIndex} className="even:bg-muted/40">
                                        {row.map((cell, cellIndex) => (
                                            <td
                                                key={cellIndex}
                                                className="whitespace-nowrap border-r border-border/50 px-2 py-1 last:border-r-0"
                                            >
                                                {cell}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {notice !== null && (
                        <p
                            data-testid="original-preview-truncated"
                            className="text-xs text-muted-foreground"
                        >
                            {notice}
                        </p>
                    )}
                </>
            )}

            {isLoading && <p className="text-xs text-muted-foreground">Loading sheet…</p>}
        </div>
    );
}
